<script lang="ts">
	import type { PromotionItem } from '$models/Block/PromotionBlock';

	interface Props {
		item: PromotionItem;
		useFilledLayout?: boolean;
	}

	const { item, useFilledLayout = true }: Props = $props();
</script>

<div
	class="promotion-item"
	class:promotion-item--filled={useFilledLayout && !item.video && item.filled !== false}
	class:promotion-item--video={item.video}
	class:has-link={item.link}
>
	{#if item.link}
		<a href={item.link.url} class="promotion-item-link" aria-label={item.title}></a>
	{/if}
	{#if item.video}
		<video class="promotion-item-video" src={item.video} autoplay muted loop playsinline></video>
	{:else}
		<figure class="promotion-item-image">
			<img
				class="promotion-item-image--desktop"
				src={item.image.url}
				width={item.image.width}
				height={item.image.height}
				alt={item.image.alt}
				loading="lazy"
				decoding="async"
			/>
			<img
				class="promotion-item-image--mobile"
				src={item.imageMobile.url}
				width={item.imageMobile.width}
				height={item.imageMobile.height}
				alt={item.imageMobile.alt}
				loading="lazy"
				decoding="async"
			/>
		</figure>
	{/if}
	<div class="promotion-item-content">
		<div class="promotion-item-info">
			{#if item.label}
				<span class="promotion-item-label">{item.label}</span>
			{/if}
			{#if item.title}
				<h3 class="promotion-item-title">{item.title}</h3>
			{/if}
			{#if item.text}
				<p class="promotion-item-text">{item.text}</p>
			{/if}
			{#if item.link}
				<div class="promotion-item-arrow"></div>
			{/if}
		</div>
	</div>
</div>

<style lang="postcss" global>
	.promotion-item {
		position: relative;
		display: flex;
		flex-direction: column;
		width: 100%;
		height: 100%;
		aspect-ratio: 9 / 10;
		background-color: var(--color-primary-light);
		border-radius: var(--radius-5);
		overflow: hidden;

		&-link {
			&:before {
				content: '';
				position: absolute;
				inset: 0;
				z-index: 2;
				cursor: pointer;
			}
		}

		&-arrow {
			display: flex;
			align-items: center;
			justify-content: center;
			width: 40px;
			height: 40px;
			background-color: var(--color-white);
			transition: background-color var(--transition);
			border-radius: 8px;

			&:before {
				content: '';
				display: block;
				width: 12px;
				height: 8px;
				background-image: url(data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTIiIGhlaWdodD0iOCIgdmlld0JveD0iMCAwIDEyIDgiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxwYXRoIGQ9Ik04IDhMNi45Mzc1IDYuOTM3NUw5LjEyNSA0Ljc1SDBMMCAzLjI1SDkuMTI1TDYuOTM3NSAxLjA2MjVMOCAwTDEyIDRMOCA4WiIgZmlsbD0iIzU5MkUyQyIvPgo8L3N2Zz4K);
				background-size: contain;
				background-repeat: no-repeat;
				background-position: center;
			}
		}

		&-image {
			order: 2;
			flex: 1 1 auto;
			display: flex;
			align-items: flex-end;
			justify-content: center;
			width: 100%;
			min-height: 0;
			height: auto;

			img {
				display: block;
				width: 100%;
				object-fit: contain;

				&.promotion-item-image--desktop {
					display: none;
				}
			}
		}

		&-video {
			display: block;
			width: 100%;
			height: 100%;
			object-fit: cover;
		}

		&-content {
			position: relative;
			order: 1;
			flex: 0 0 auto;
			display: flex;
			flex-direction: column;
			padding: 40px 24px;
		}

		&-info {
			display: flex;
			flex-direction: column;
			gap: 8px;
		}

		&-label {
			display: block;
			font-size: 14px;
			text-transform: uppercase;
			font-family: var(--font-secondary);
			color: var(--color-brown);
			line-height: 1;
		}

		&-title {
			font-size: 24px;
			line-height: 1;
			margin-bottom: 8px;
		}

		&-text {
			margin-bottom: 8px;
		}

		&.has-link {
			cursor: pointer;
		}

		&.has-link:hover &-arrow {
			background-color: var(--color-primary-dark);
		}

		&--filled {
			display: grid;
			aspect-ratio: 2 / 3;
			background-color: transparent;

			& > * {
				grid-column: 1;
				grid-row: 1;
			}

			.promotion-item-content {
				order: unset;
				flex: unset;
				z-index: 1;
			}

			.promotion-item-image {
				order: unset;
				flex: unset;
				display: block;
				height: 100%;

				img {
					width: 100%;
					height: 100%;
					object-fit: cover;
				}
			}
		}

		&--video {
			display: grid;
			aspect-ratio: 16 / 9;
			background-color: transparent;

			& > * {
				grid-column: 1;
				grid-row: 1;
			}

			.promotion-item-content {
				order: unset;
				flex: unset;
				z-index: 1;
			}
		}

		@media (--md) {
			&-title {
				font-size: 32px;
			}

			&-text {
				font-size: 18px;
			}
		}

		@media (--lg) {
			.promotion-inner--count-2 &--filled {
				aspect-ratio: 1;
			}

			&-image {
				img {
					&.promotion-item-image--desktop {
						display: block;
					}

					&.promotion-item-image--mobile {
						display: none;
					}
				}
			}
		}

		@media (--xl) {
			&-title {
				font-size: 36px;
			}
		}
	}
</style>
